import { sanitizeUrl } from '@braintree/sanitize-url';
import { useObserver } from 'mobx-react-lite';
import { NextRouter } from 'next/router';
import useStores from 'shared/hooks/use-stores';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useEnterpriseMobileAppSettingsQuery } from 'types/graphql';

const internalApps = [
  'com.dutchie.retailer',
  // Android does not permit hyphens
  'com.dutchie.retailer-sample',
];

type Params = {
  token?: string;
  query?: NextRouter['query'];
};

export function useRedirectUrlForMobileEcomm({ query, token }: Params): string | null {
  const { apolloClient, UI } = useStores();
  const { dispensary } = useDispensary();
  const isMobileEcommApp = useObserver(() => UI.isMobileEcommApp);
  const enterpriseId = dispensary?.retailer?.enterpriseId;

  const { data } = useEnterpriseMobileAppSettingsQuery({
    variables: { enterpriseId },
    client: apolloClient,
    skip: !enterpriseId || !isMobileEcommApp,
  });

  if (!token?.length) {
    return null;
  }

  const redirectUrl = sanitizeUrl(query?.redirectUrl as string);
  const shouldRedirect = isMobileEcommApp && !!redirectUrl.length;
  if (!shouldRedirect) {
    return null;
  }

  const allowedIdentifiers = [...internalApps];
  if (data?.enterpriseMobileAppSettings?.iosBundleIdentifier) {
    allowedIdentifiers.push(data.enterpriseMobileAppSettings.iosBundleIdentifier.toLowerCase());
  }
  if (data?.enterpriseMobileAppSettings?.androidPackageName) {
    allowedIdentifiers.push(data.enterpriseMobileAppSettings.androidPackageName.toLowerCase());
  }

  const allowedRedirectPrefixes = ['https://dutchie.com/', ...allowedIdentifiers.map((s) => `${s}://`)];

  const redirectAllowed = allowedRedirectPrefixes.some((s) => redirectUrl.startsWith(s));
  if (!redirectAllowed) {
    return null;
  }

  return `${redirectUrl}?token=${token}`;
}
